export function initializeTextDecalEditor(texturePainter) {
    const textDecalModal = document.getElementById('textDecalModal');
    const textDecalButton = document.getElementById('textDecalButton');
    // Scope the close button to the text decal modal so we don't grab other .close-button elements (e.g., tattoo modal)
    const closeButton = textDecalModal.querySelector('.close-button');
    const generateTextDecalButton = document.getElementById('generateTextDecalButton');
    const textDecalInput = document.getElementById('textDecalInput');
    const fontSizeSlider = document.getElementById('fontSizeSlider');
    const fontColorPicker = document.getElementById('fontColorPicker');
    const fontFamilySelector = document.getElementById('fontFamilySelector');
    const textDecalPreview = document.getElementById('textDecalPreview');
    const previewCtx = textDecalPreview.getContext('2d');
    const loadSystemFontsButton = document.getElementById('loadSystemFonts');
    const kerningSlider = document.getElementById('kerningSlider');
    const boldCheckbox = document.getElementById('boldCheckbox');
    const italicCheckbox = document.getElementById('italicCheckbox');
    const underlineCheckbox = document.getElementById('underlineCheckbox');

    const RENDER_RESOLUTION = 1024; 
    const PREVIEW_DISPLAY_SIZE = 256; // The size the canvas is displayed at in CSS
    const scaleFactor = RENDER_RESOLUTION / PREVIEW_DISPLAY_SIZE;

    textDecalPreview.width = RENDER_RESOLUTION;
    textDecalPreview.height = RENDER_RESOLUTION;

    textDecalButton.addEventListener('click', () => {
        textDecalModal.style.display = 'flex';
        // Pause global hotkeys while text decal editor is open
        window.hotkeysPaused = true;
        updateTextDecalPreview();
    });

    closeButton.addEventListener('click', () => {
        textDecalModal.style.display = 'none';
        // Resume global hotkeys when closing editor
        window.hotkeysPaused = false;
    });

    // Click outside the modal content closes only this modal
    window.addEventListener('click', (event) => {
        if (event.target === textDecalModal) {
            textDecalModal.style.display = 'none';
            window.hotkeysPaused = false;
        }
    });

    generateTextDecalButton.addEventListener('click', () => {
        const dataUrl = textDecalPreview.toDataURL();
        texturePainter.addDecalToPicker(dataUrl);
        textDecalModal.style.display = 'none';
        // Resume global hotkeys after generating decal and closing modal
        window.hotkeysPaused = false;
    });

    textDecalInput.addEventListener('input', updateTextDecalPreview);
    fontSizeSlider.addEventListener('input', updateTextDecalPreview);
    fontColorPicker.addEventListener('input', updateTextDecalPreview);
    fontFamilySelector.addEventListener('change', updateTextDecalPreview);
    kerningSlider.addEventListener('input', updateTextDecalPreview);
    boldCheckbox.addEventListener('change', updateTextDecalPreview);
    italicCheckbox.addEventListener('change', updateTextDecalPreview);
    underlineCheckbox.addEventListener('change', updateTextDecalPreview);

    loadSystemFontsButton.addEventListener('click', async () => {
        if ('queryLocalFonts' in window) {
            try {
                const availableFonts = await window.queryLocalFonts();
                const uniqueFontFamilies = [...new Set(availableFonts.map(font => font.family))];
                
                fontFamilySelector.innerHTML = ''; 
                uniqueFontFamilies.sort().forEach(fontFamily => {
                    const option = document.createElement('option');
                    option.value = fontFamily;
                    option.textContent = fontFamily;
                    fontFamilySelector.appendChild(option);
                });
                updateTextDecalPreview();
            } catch (err) {
                console.error('Font Access API error:', err);
                alert('Could not access local fonts. Please ensure you grant permission.');
            }
        } else {
            alert('Your browser does not support the Font Access API. Please use a modern browser like Chrome or Edge.');
        }
    });

    function updateTextDecalPreview() {
        const text = textDecalInput.value || "Sample Text";
        const fontSize = parseInt(fontSizeSlider.value) * scaleFactor;
        const fontColor = fontColorPicker.value;
        const selectedFamily = fontFamilySelector.value && fontFamilySelector.value.trim().length ? fontFamilySelector.value : '';
        const kerning = parseInt(kerningSlider.value, 10);
        const isBold = boldCheckbox.checked;
        const isItalic = italicCheckbox.checked;
        const isUnderline = underlineCheckbox.checked;

        previewCtx.clearRect(0, 0, textDecalPreview.width, textDecalPreview.height);

        // Build a robust system font stack so Canvas uses proportional system fonts.
        // Quote the selected family if it contains spaces.
        const quoteIfNeeded = (name) => (name && /\s/.test(name) ? `"${name}"` : name);
        const systemStack = 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif';
        const familyStack = selectedFamily ? `${quoteIfNeeded(selectedFamily)}, ${systemStack}` : systemStack;

        // Compose style and set before measureText/fillText
        let fontStyle = '';
        if (isItalic) fontStyle += 'italic ';
        if (isBold) fontStyle += 'bold ';

        previewCtx.font = `${fontStyle}${Math.max(1, Math.floor(fontSize))}px ${familyStack}`;
        previewCtx.fillStyle = fontColor;
        // Use alphabetic baseline for natural text placement; we will compute line boxes using ascent/descent.
        previewCtx.textBaseline = 'alphabetic';
        previewCtx.textAlign = 'left';

        const lines = text.split('\n');

        // Derive ascent/descent if available for better line spacing and underline placement.
        const sampleMetrics = previewCtx.measureText('Mg');
        const ascent = sampleMetrics.actualBoundingBoxAscent || fontSize * 0.8;
        const descent = sampleMetrics.actualBoundingBoxDescent || fontSize * 0.2;
        const naturalLineHeight = ascent + descent;
        const lineHeight = Math.max(naturalLineHeight * 1.2, fontSize * 1.2);
        // Kerning scale: UI slider is in px at preview display scale; convert to canvas px (render scaleFactor)
        const kerningPx = kerning * scaleFactor;

        // Compute total block height using lineHeight, center vertically based on baseline
        const totalTextHeight = lines.length * lineHeight;
        let baselineY = (textDecalPreview.height - totalTextHeight) / 2 + ascent;

        lines.forEach(line => {
            // Measure width: either per-character (for custom kerning slider) or whole line
            let lineActualWidth = 0;
            if (kerningPx !== 0) {
                let totalCharactersWidth = 0;
                for (const ch of line) {
                    totalCharactersWidth += previewCtx.measureText(ch).width;
                }
                const extra = line.length > 1 ? (line.length - 1) * kerningPx : 0;
                lineActualWidth = totalCharactersWidth + extra;
            } else {
                lineActualWidth = previewCtx.measureText(line).width;
            }

            // Center horizontally
            const lineStartX = (textDecalPreview.width - lineActualWidth) / 2;

            if (kerningPx !== 0) {
                // Manual spacing rendering with custom kerning in canvas pixels
                let penX = lineStartX;
                for (const ch of line) {
                    previewCtx.fillText(ch, penX, baselineY);
                    penX += previewCtx.measureText(ch).width + kerningPx;
                }
            } else {
                // Single draw from computed left X
                previewCtx.fillText(line, lineStartX, baselineY);
            }

            if (isUnderline) {
                const underlineOffset = descent * 0.5; // place between baseline and descent for a balanced underline
                const underlineY = baselineY + underlineOffset;
                previewCtx.beginPath();
                previewCtx.strokeStyle = fontColor;
                previewCtx.lineWidth = Math.max(1, fontSize * 0.05);
                previewCtx.moveTo(lineStartX, underlineY);
                previewCtx.lineTo(lineStartX + lineActualWidth, underlineY);
                previewCtx.stroke();
            }

            baselineY += lineHeight;
        });
    }
}
